// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, pure } from '../../../node_module_clones/recompose';
import classNames from 'classnames';

// Selectors
import mainSelector from './mainSelector';

// Components
import InboxContainer from '../../inbox/InboxContainer';
import MobileInboxContainer from '../../mobile/inbox/MobileInboxContainer';
import CanvasContainer from '../../canvas/Canvas';
import PointerLayer from '../../components/pointsOfInterest/pointers/PointerLayer';
import MainLayout from './MainLayout';
import CanvasErrorBoundary from '../../canvas/error/CanvasErrorBoundary';
import InboxErrorBoundary from '../../inbox/error/InboxErrorBoundary';
import { MilanoteApplicationMode } from '../../../common/platform/platformTypes';

const enhance = compose(connect(mainSelector), pure);

const Main = (props) => {
    const {
        isDragging,
        isEditable,
        isInboxOpen,
        isAttachMode,
        currentBoardId,
        currentBoard,
        platformDetails,
        gridSize,
    } = props;

    const classes = {
        'is-dragging': isDragging,
        'no-toolbar': !isEditable,
        editable: isEditable,
        'not-editable': !isEditable,
        'inbox-open': isInboxOpen,
    };

    const mainRef = React.useRef();

    // Add the "is-dragging-commit" one frame after the drag has started, this allows us to disable pointer-events
    // on elements that interfere with drop targets, while still allowing elements to be dragged in the first place.
    // E.g. The "TaskDragHandle" needs to accept pointer events to start the drag of a task, however it can get in
    // the way of attaching comments onto tasks.  If we just use "is-dragging" it will cause the drag to fail because
    // the drag handle will stop accepting pointer events before React DnD starts rendering the drag.
    // This is also an issue with HotSpots
    React.useEffect(() => {
        requestAnimationFrame(() => {
            if (mainRef.current && isDragging) {
                mainRef.current.classList.add('is-dragging-commit');

                if (isAttachMode) {
                    mainRef.current.classList.add('ws-is-attach-mode');
                } else {
                    mainRef.current.classList.remove('ws-is-attach-mode');
                }
            }
        });
    }, [isDragging, isAttachMode]);

    if (!currentBoardId || !currentBoard) return null;

    const InboxComponent =
        platformDetails.appMode === MilanoteApplicationMode.mobile ? MobileInboxContainer : InboxContainer;

    return (
        <MainLayout
            ref={mainRef}
            className={classNames(classes)}
            canvasComponent={
                <React.Fragment>
                    <CanvasErrorBoundary currentBoardId={currentBoardId}>
                        {/* Explicitly removing isDragging to prevent unnecessary re-renders of context */}
                        <CanvasContainer {...props} isDragging={null} />
                    </CanvasErrorBoundary>
                    <PointerLayer currentBoardId={currentBoardId} gridSize={gridSize} />
                </React.Fragment>
            }
            inboxComponent={
                <InboxErrorBoundary currentBoardId={currentBoardId}>
                    <InboxComponent
                        {...props}
                        platformDetails={platformDetails}
                        isDragging={null}
                        retainPreviousVisibleElements
                        key={currentBoardId /* Force re-render on board change */}
                    />
                </InboxErrorBoundary>
            }
        />
    );
};

Main.propTypes = {
    isInboxOpen: PropTypes.bool,
    isDragging: PropTypes.bool,
    isAttachMode: PropTypes.bool,
    isEditable: PropTypes.bool,
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    canvasElementIds: PropTypes.array,
    gridSize: PropTypes.number,
    platformDetails: PropTypes.object,
};

export default enhance(Main);
